import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  AcademicCapIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  LockClosedIcon,
  PhoneIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { showError, showSuccess, showWarning } from "../../utils/alert";

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  password: "",
  confirm_password: "",
  setup_key: "",
};

export default function SuperAdminRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!form.first_name.trim() || !form.last_name.trim()) {
      await showWarning({ title: "Name Required", text: "Enter your first and last name." });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      await showWarning({ title: "Valid Email Required", text: "Enter a valid email address." });
      return;
    }
    if (form.password.length < 8) {
      await showWarning({ title: "Password Too Short", text: "Use at least 8 characters." });
      return;
    }
    if (form.password !== form.confirm_password) {
      await showWarning({ title: "Passwords Do Not Match", text: "Confirm your password correctly." });
      return;
    }
    if (!form.setup_key.trim()) {
      await showWarning({ title: "Setup Key Required", text: "Enter the setup key configured on the server." });
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post("/super-admin/register", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        password: form.password,
        setup_key: form.setup_key,
      });
      await showSuccess({
        title: "Account Created",
        text: "Your Super Admin account is ready. Sign in to open the platform dashboard.",
      });
      // Redirect to the main login page – the super-admin/login route no longer exists.
      navigate("/login", { replace: true });
    } catch (error) {
      await showError({
        title: "Registration Failed",
        text: error?.response?.data?.message || "Unable to create the Super Admin account.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-100";

  return (
    <>
      <Helmet>
        <title>Super Admin Registration | PawaSchool</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-lg">
          {/* Simple header */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 text-primary-700">
              <AcademicCapIcon className="h-10 w-10" />
              <span className="text-2xl font-bold">PawaSchool</span>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-slate-900">Create Super Admin Account</h2>
            <p className="mt-1 text-sm text-slate-500">Set up the platform administrator</p>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60 sm:p-8">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  First name
                  <span className="relative mt-1.5 block">
                    <UserIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      name="first_name"
                      value={form.first_name}
                      onChange={updateField}
                      className={inputClass}
                      placeholder="First name"
                      autoComplete="given-name"
                    />
                  </span>
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Last name
                  <span className="relative mt-1.5 block">
                    <UserIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      name="last_name"
                      value={form.last_name}
                      onChange={updateField}
                      className={inputClass}
                      placeholder="Last name"
                      autoComplete="family-name"
                    />
                  </span>
                </label>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Email address
                <span className="relative mt-1.5 block">
                  <EnvelopeIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={updateField}
                    className={inputClass}
                    placeholder="admin@example.com"
                    autoComplete="email"
                  />
                </span>
              </label>

              <label className="block text-sm font-medium text-slate-700">
                Phone number <span className="font-normal text-slate-400">(optional)</span>
                <span className="relative mt-1.5 block">
                  <PhoneIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={updateField}
                    className={inputClass}
                    placeholder="+232 76 123456"
                    autoComplete="tel"
                  />
                </span>
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-700">
                  Password
                  <span className="relative mt-1.5 block">
                    <LockClosedIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={updateField}
                      className={`${inputClass} pr-11`}
                      placeholder="Min 8 characters"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </span>
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Confirm password
                  <span className="relative mt-1.5 block">
                    <LockClosedIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="confirm_password"
                      value={form.confirm_password}
                      onChange={updateField}
                      className={inputClass}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                    />
                  </span>
                </label>
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Server setup key
                <span className="relative mt-1.5 block">
                  <KeyIcon className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    name="setup_key"
                    value={form.setup_key}
                    onChange={updateField}
                    className={inputClass}
                    placeholder="Enter the setup key"
                    autoComplete="off"
                  />
                </span>
                {/* ✅ No extra instruction text */}
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 flex w-full items-center justify-center rounded-xl bg-primary-600 px-5 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Creating account..." : "Create Super Admin account"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-primary-700 hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
    </>
  );
}